import { Avatar } from '@elementary/common-ui/components';
import {
  INCIDENT_GRAPHQL_DETAIL,
  INCIDENT_LIST_FILTER_OPTIONS,
  INCIDENT_LIST_GROUP_SUMMARY,
  INCIDENT_LIST_PAGED,
  INCIDENT_MERGE_CARDS,
  TREE_INCIDENT_STATUS_TOTALS,
  USERS,
} from '@elementary/common-ui/constants/queryKeys';
import useEnvPermission from '@elementary/common-ui/hooks/useEnvPermission';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchAssignees } from 'incidents/api';
import { NO_ASSIGNEE_VALUE } from 'incidents/constants';
import type { Severity, Status } from 'incidents/types';
import { severityIconMap, statusIconMap } from 'incidents/utils';
import useEnvironments from 'shared/hooks/useEnvironments';
import { ReactComponent as UserIcon } from '@elementary/common-ui/assets/16x16/user.svg';
import type { IncidentSeverity, IncidentStatus } from '../types';
import {
  updateIncidentStatus,
  updateIncidentAssignee,
  updateIncidentSeverity,
} from '../api';
import { SEVERITY_DISPLAY, STATUS_DISPLAY } from '../constants';

const SEVERITY_VALUES: IncidentSeverity[] = [
  'LOW',
  'NORMAL',
  'HIGH',
  'CRITICAL',
];

const STATUS_VALUES: IncidentStatus[] = ['OPEN', 'ACKNOWLEDGED', 'RESOLVED'];

const useIncidentMetadata = () => {
  const { selectedEnvId } = useEnvironments();
  const hasPermission = useEnvPermission();
  const queryClient = useQueryClient();

  const { data: usersData } = useQuery([USERS, selectedEnvId], () =>
    fetchAssignees(selectedEnvId!),
  );

  const invalidateAll = () => {
    queryClient.invalidateQueries([INCIDENT_LIST_PAGED], { exact: false });
    queryClient.invalidateQueries([INCIDENT_LIST_GROUP_SUMMARY], {
      exact: false,
    });
    queryClient.invalidateQueries([INCIDENT_LIST_FILTER_OPTIONS], {
      exact: false,
    });
    queryClient.invalidateQueries([INCIDENT_GRAPHQL_DETAIL], { exact: false });
    queryClient.invalidateQueries([INCIDENT_MERGE_CARDS], { exact: false });
    queryClient.invalidateQueries([TREE_INCIDENT_STATUS_TOTALS], {
      exact: false,
    });
  };

  const canEdit = hasPermission('EDIT_INCIDENTS');

  const { mutateAsync: updateStatus, isLoading: isStatusUpdating } =
    useMutation(
      async ({
        incidentIds,
        status,
      }: {
        incidentIds: string[];
        status: string;
      }) => {
        if (selectedEnvId && canEdit) {
          return updateIncidentStatus(selectedEnvId, incidentIds, status);
        }
      },
      { onSuccess: invalidateAll },
    );

  const { mutateAsync: updateAssignee, isLoading: isAssigneeUpdating } =
    useMutation(
      async ({
        incidentIds,
        assignee,
      }: {
        incidentIds: string[];
        assignee: string | null;
      }) => {
        if (selectedEnvId && canEdit) {
          return updateIncidentAssignee(selectedEnvId, incidentIds, assignee);
        }
      },
      { onSuccess: invalidateAll },
    );

  const { mutateAsync: updateSeverity, isLoading: isSeverityUpdating } =
    useMutation(
      async ({
        incidentIds,
        severity,
      }: {
        incidentIds: string[];
        severity: string;
      }) => {
        if (selectedEnvId && canEdit) {
          return updateIncidentSeverity(selectedEnvId, incidentIds, severity);
        }
      },
      { onSuccess: invalidateAll },
    );

  const isUpdating =
    isStatusUpdating || isAssigneeUpdating || isSeverityUpdating;

  const severityOptions = SEVERITY_VALUES.map((value) => {
    const label = SEVERITY_DISPLAY[value.toLowerCase()] ?? value;
    return {
      label,
      value,
      icon: severityIconMap[label as Severity],
    };
  });

  const statusOptions = STATUS_VALUES.map((value) => {
    const label = STATUS_DISPLAY[value.toLowerCase()] ?? value;
    return {
      label,
      value,
      icon: statusIconMap[label as Status],
    };
  });

  const assigneeOptions = [
    {
      label: 'Unassigned',
      value: NO_ASSIGNEE_VALUE,
      icon: <UserIcon />,
    },
    ...(usersData
      ?.sort((a, b) => a.name.localeCompare(b.name))
      .map((u) => ({
        label: u.name,
        value: u.email,
        icon: <Avatar value={{ id: u.id, name: u.name }} size="small" />,
      })) ?? []),
  ];

  return {
    usersData,
    severityOptions,
    statusOptions,
    assigneeOptions,
    canEdit,
    updateStatus,
    updateAssignee,
    updateSeverity,
    isUpdating,
  };
};

export default useIncidentMetadata;
